import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Phone, WhatsApp, Plus, X, CommentDots } from '@/components/ui/Icons';
import { company } from '@/data/company';

/**
 * Sticky FAB cluster: phone, WhatsApp, "Get a quote".
 * Collapsed to a single trigger; expands on tap into a vertical stack.
 * Hidden on /admin and on /contact (the contact page already IS the CTA).
 */
export function FloatingActions() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Show after the user has scrolled a bit so it doesn't fight the hero CTAs.
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (location.pathname.startsWith('/admin')) return null;

  const actions = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: WhatsApp,
      href: `https://wa.me/${company.whatsappTel}`,
      external: true,
      colour: 'bg-[#25D366] text-white',
    },
    {
      key: 'phone',
      label: 'Call',
      icon: Phone,
      href: `tel:${company.phoneTel}`,
      external: false,
      colour: 'bg-navy-800 text-paper-0',
    },
    {
      key: 'quote',
      label: 'Quote',
      icon: CommentDots,
      to: '/contact',
      colour: 'bg-gold-400 text-navy-900',
    },
  ] as const;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="fab"
          initial={{ opacity: 0, scale: 0.8, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 12 }}
          transition={{ duration: 0.25 }}
          className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6"
          aria-label="Quick contact actions"
        >
          <AnimatePresence>
            {open && (
              <motion.ul
                key="stack"
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={{
                  hidden: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
                  show: { transition: { staggerChildren: 0.06 } },
                }}
                className="flex flex-col items-end gap-2.5"
              >
                {actions.map((a) => (
                  <motion.li
                    key={a.key}
                    variants={{
                      hidden: { opacity: 0, y: 12, scale: 0.85 },
                      show: { opacity: 1, y: 0, scale: 1 },
                    }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {'href' in a ? (
                      <a
                        href={a.href}
                        target={a.external ? '_blank' : undefined}
                        rel={a.external ? 'noopener noreferrer' : undefined}
                        className={`group flex items-center gap-3 rounded-full pl-4 pr-3 py-2.5 shadow-[0_12px_28px_-10px_rgba(7,15,31,0.6)] transition hover:-translate-y-0.5 ${a.colour}`}
                      >
                        <span className="text-sm font-semibold tracking-wide">{a.label}</span>
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15 backdrop-blur-xs">
                          <a.icon className="h-4 w-4" />
                        </span>
                      </a>
                    ) : (
                      <Link
                        to={a.to}
                        className={`group flex items-center gap-3 rounded-full pl-4 pr-3 py-2.5 shadow-[0_12px_28px_-10px_rgba(7,15,31,0.6)] transition hover:-translate-y-0.5 ${a.colour}`}
                      >
                        <span className="text-sm font-semibold tracking-wide">{a.label}</span>
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-navy-900/15 backdrop-blur-xs">
                          <a.icon className="h-4 w-4" />
                        </span>
                      </Link>
                    )}
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>

          <motion.button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? 'Close quick actions' : 'Open quick actions'}
            initial={false}
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="group relative grid h-14 w-14 place-items-center rounded-full bg-gold-400 text-navy-900 shadow-[0_12px_32px_-8px_rgba(184,145,70,0.7)] outline-hidden ring-1 ring-gold-300/60 transition hover:bg-gold-300 focus-visible:ring-4 focus-visible:ring-gold-300/40"
          >
            {/* Pulsing ring */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-full bg-gold-400 opacity-40 animate-[fabpulse_1.8s_ease-in-out_infinite]"
            />
            <AnimatePresence mode="wait" initial={false}>
              {open ? (
                <motion.span
                  key="x"
                  initial={{ rotate: -45, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 45, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                >
                  <X className="h-5 w-5" />
                </motion.span>
              ) : (
                <motion.span
                  key="plus"
                  initial={{ rotate: 45, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -45, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                >
                  <Plus className="h-5 w-5" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          <style>{`
            @keyframes fabpulse {
              0%, 100% { transform: scale(1); opacity: 0.5; }
              50%      { transform: scale(1.35); opacity: 0; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
